import {useState, useEffect, useContext} from "react";
import {Box, Switch, Button, Typography, CircularProgress, Alert} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import {PageBase} from "@/app2/shared/layouts/PageBase";
import {useGetMe, useUpdateSettings, UpdateSettingsBody} from "@/app2/shared/data";
import {LocaleContext} from "@/app2/shared/contexts";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {formatBuildMetadataLine, useBuildMetadata} from "@/hooks/useBuildMetadata";
import useDevCycleSdk, {formatEnabledRemoteDevCycleFlags} from "@/hooks/useDevCycleSdk";
import {SettingsBuildInfoCollapsible} from "@/components/settings/SettingsBuildInfoCollapsible";

/**
 * Settings Page (App2)
 * 
 * User training preferences including:
 * - Language selection (English/Spanish)
 * - Notification settings (currently hidden)
 * - Reminder settings (currently hidden)
 * - Link to PropelAuth account management
 */
export const SettingsPage = () => {
	const {t} = useSSTranslation();
	const [, setLocale] = useContext(LocaleContext);
	const buildMeta = useBuildMetadata();
	const buildMetaLine = formatBuildMetadataLine(buildMeta);
	const devCycleFlags = useDevCycleSdk();
	const remoteFlagsLine = formatEnabledRemoteDevCycleFlags(devCycleFlags);

	// Fetch current user settings
	const {data: userData, isLoading: isLoadingUser} = useGetMe();

	// Settings mutation
	const {mutate: updateSettings, isPending: isUpdating, isSuccess, isError, error} = useUpdateSettings();

	// Local form state
	const [formData, setFormData] = useState<UpdateSettingsBody>({
		preferredLanguage: "en",
		notifyInApp: false,
		notifyEmail: false,
		dailySkillsReminder: false,
		overDueSkilsReminder: false
	});

	// Initialize form with user data (API uses en | es-419)
	useEffect(() => {
		if (userData) {
			setFormData({
				preferredLanguage: userData.preferredLanguage,
				notifyInApp: userData.notifyInApp,
				notifyEmail: userData.notifyEmail,
				dailySkillsReminder: userData.dailySkillsReminder,
				overDueSkilsReminder: userData.overDueSkilsReminder
			});
		}
	}, [userData]);

	// Sync context when settings saved (context accepts en | es-419)
	useEffect(() => {
		if (isSuccess && formData.preferredLanguage) {
			setLocale({locale: formData.preferredLanguage});
		}
	}, [isSuccess, formData.preferredLanguage, setLocale]);

	// Check if changes were made
	const hasChanges = userData && (
		formData.preferredLanguage !== userData.preferredLanguage ||
		formData.notifyInApp !== userData.notifyInApp ||
		formData.notifyEmail !== userData.notifyEmail ||
		formData.dailySkillsReminder !== userData.dailySkillsReminder ||
		formData.overDueSkilsReminder !== userData.overDueSkilsReminder
	);

	const handleSave = () => {
		if (!hasChanges || isUpdating) return;
		updateSettings(formData);
	};

	const handleDiscard = () => {
		if (!userData) return;
		setFormData({
			preferredLanguage: userData.preferredLanguage,
			notifyInApp: userData.notifyInApp,
			notifyEmail: userData.notifyEmail,
			dailySkillsReminder: userData.dailySkillsReminder,
			overDueSkilsReminder: userData.overDueSkilsReminder
		});
	};

	const handleManageAccount = () => {
		window.location.href = process.env.REACT_APP_PROPELAUTH_ACCOUNT_SETTINGS_URI ?? "";
	};

	const languageLabelButtonSx = (selected: boolean) => ({
		cursor: "pointer",
		border: "none",
		background: "none",
		p: 0,
		font: "inherit",
		textAlign: "left" as const,
		color: selected ? "primary.main" : "text.primary",
		fontWeight: selected ? 600 : 400,
		textDecoration: "underline",
		textDecorationColor: "transparent",
		transition: "color 0.15s ease, text-decoration-color 0.15s ease",
		"&:hover": {
			textDecorationColor: "currentColor"
		},
		"&:focus-visible": {
			outline: "2px solid",
			outlineColor: "primary.main",
			outlineOffset: 2,
			borderRadius: 0.5
		}
	});

	return (
		<PageBase contentWidth="860px" disableSideBar>
			<Box sx={{py: 5, px: 2.5}}>
				<Typography variant="h4" component="h1" sx={{mb: 3, fontWeight: 600}}>
					{t("page_settings_title", "Training Preferences")}
				</Typography>

				{/* Loading State */}
				{isLoadingUser && (
					<Box sx={{display: "flex", justifyContent: "center", py: 4}}>
						<CircularProgress />
					</Box>
				)}

				{/* Success Message */}
				{isSuccess && (
					<Alert severity="success" sx={{mb: 3}}>
						{t("notifications_settings_updated", "Your details have been updated")}
					</Alert>
				)}

				{/* Error Message */}
				{isError && (
					<Alert severity="error" sx={{mb: 3}}>
						{error?.message || "Failed to update settings"}
					</Alert>
				)}

				{/* Settings Form */}
				{userData && (
					<Box
						sx={{
							borderRadius: 2,
							p: 2.5,
							border: 1,
							borderColor: "divider",
							bgcolor: "background.paper"
						}}
					>
						{/* Language Preference */}
						<Box sx={{mb: 3}}>
							<Typography id="language-preference-heading" variant="h6" sx={{mb: 2}}>
								{t("page_settings_language", "Preferred language")}:
							</Typography>
							<Box
								role="group"
								aria-labelledby="language-preference-heading"
								sx={{display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap"}}
							>
								<Typography
									component="button"
									type="button"
									variant="body1"
									aria-pressed={formData.preferredLanguage === "en"}
									data-test-id="settings-language-english"
									onClick={() =>
										setFormData((prev) => ({
											...prev,
											preferredLanguage: "en"
										}))
									}
									sx={languageLabelButtonSx(formData.preferredLanguage === "en")}
								>
									{t("page_settings_english", "English")}
								</Typography>
								<Switch
									checked={formData.preferredLanguage === "es-419"}
									onChange={(e) =>
										setFormData((prev) => ({
											...prev,
											preferredLanguage: e.target.checked ? "es-419" : "en"
										}))
									}
									data-test-id="settings-language-switch"
									aria-label={t("page_settings_language", "Preferred language")}
									inputProps={{"aria-describedby": "language-switch-description"}}
								/>
								<Typography
									component="button"
									type="button"
									variant="body1"
									aria-pressed={formData.preferredLanguage === "es-419"}
									data-test-id="settings-language-spanish"
									onClick={() =>
										setFormData((prev) => ({
											...prev,
											preferredLanguage: "es-419"
										}))
									}
									sx={languageLabelButtonSx(formData.preferredLanguage === "es-419")}
								>
									{t("page_settings_spanish", "Spanish")}
								</Typography>
								<Typography id="language-switch-description" sx={{display: "none"}}>
									Toggle between English and Spanish language preferences
								</Typography>
							</Box>
						</Box>

						{/* Manage Account Link */}
						<Box
							onClick={handleManageAccount}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									handleManageAccount();
								}
							}}
							role="button"
							tabIndex={0}
							data-test-id="settings-manage-account-link"
							aria-label={t("page_settings_manage_account", "Manage account details")}
							sx={{
								display: "flex",
								gap: 1.25,
								cursor: "pointer",
								mb: 3,
								"&:hover": {
									opacity: 0.7
								}
							}}
						>
							<PersonIcon color="primary" />
							<Typography>{t("page_settings_manage_account", "Manage account details")}</Typography>
						</Box>

						{/* Action Buttons */}
						<Box
							role="group"
							aria-label="Language preferences actions"
							sx={{
								display: "flex",
								gap: 1.75,
								pt: 2.5,
								mt: 2.5,
								borderTop: 1,
								borderColor: "divider"
							}}
						>
							<Button
								variant="contained"
								disabled={!hasChanges || isUpdating}
								onClick={handleSave}
								data-test-id="settings-save-button"
								aria-label={t("page_settings_save_button", "Save")}
								sx={{
									minWidth: 120,
									textTransform: "uppercase",
									fontWeight: 600,
									fontSize: "0.875rem"
								}}
							>
								{isUpdating ? (
									<CircularProgress size={20} color="inherit" />
								) : (
									t("page_settings_save_button", "Save")
								)}
							</Button>

							<Button
								variant="outlined"
								disabled={!hasChanges || isUpdating}
								onClick={handleDiscard}
								data-test-id="settings-discard-button"
								aria-label={t("page_settings_cancel_button", "Discard")}
								sx={{
									minWidth: 120,
									textTransform: "uppercase",
									fontWeight: 600,
									fontSize: "0.875rem"
								}}
							>
								{t("page_settings_cancel_button", "Discard")}
							</Button>
						</Box>
					</Box>
				)}

				<SettingsBuildInfoCollapsible
					buildMetaLine={buildMetaLine}
					remoteFlagsLine={remoteFlagsLine}
					variant="app2"
				/>
			</Box>
		</PageBase>
	);
};

